/**
 * Bulk match scoring (Phase 3) — admin button to score one deal against
 * every investor's buyer profile, with a paginated results table. The run
 * is async: clicking the button enqueues an `ApiEvent` of type
 * `bulk_match_scoring`; this component polls until the run finishes, then
 * loads the persisted per-investor rows. Each row expands inline using the
 * existing HeaderScore / SpiderChart / CategoryBreakdownTable components
 * (no re-score — the persisted `MatchResult` jsonb is rendered).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon, Label, Message, Table } from 'semantic-ui-react';
import { Button } from '~/components';
import {
  BulkMatchScoreRow,
  listBulkMatchScores,
  pollBulkMatchScoring,
  startBulkMatchScoring,
} from '~/api/buyerProfile';
import HeaderScore from './HeaderScore';
import SpiderChart from './SpiderChart';
import CategoryBreakdownTable from './CategoryBreakdownTable';
import NotesSection from './NotesSection';
import type { DealCategory, ExclusionBand } from '../types';

interface Props {
  projectId?: string;
  portfolioId?: string;
  labelFor: (questionKey: string) => string;
  categoryFor: (questionKey: string) => DealCategory | undefined;
}

const POLL_INTERVAL_MS = 1500;
const PAGE_SIZE = 25;

const bandColor = (band: ExclusionBand): 'red' | 'yellow' | 'green' =>
  band === 'red' ? 'red' : band === 'yellow' ? 'yellow' : 'green';

const scoreColor = (score: number): 'grey' | 'yellow' | 'green' => {
  if (score < 50) return 'grey';
  if (score < 70) return 'yellow';
  return 'green';
};

export default function AllInvestorsMatchTable({
  projectId,
  portfolioId,
  labelFor,
  categoryFor,
}: Props) {
  const dealParams = useMemo(
    () => ({
      ...(projectId ? { projectId } : {}),
      ...(portfolioId ? { portfolioId } : {}),
    }),
    [projectId, portfolioId],
  );

  const [runStatus, setRunStatus] = useState<
    'idle' | 'running' | 'loading' | 'ready' | 'error'
  >('idle');
  const [progress, setProgress] = useState<{ total?: number; processed?: number }>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [rows, setRows] = useState<BulkMatchScoreRow[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [apiEventId, setApiEventId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load whatever the latest persisted run was when the panel mounts, so
  // admins see prior runs without needing to re-trigger.
  useEffect(() => {
    if (!projectId && !portfolioId) return;
    loadPage(1, undefined);
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, portfolioId]);

  const loadPage = async (nextPage: number, pinApiEventId: string | undefined) => {
    setRunStatus((s) => (s === 'idle' || s === 'ready' ? 'loading' : s));
    try {
      const res = await listBulkMatchScores({
        ...dealParams,
        ...(pinApiEventId ? { apiEventId: pinApiEventId } : {}),
        page: nextPage,
        pageSize: PAGE_SIZE,
      });
      setRows(res.rows);
      setTotal(res.total);
      setPage(res.page);
      setApiEventId(res.apiEventId);
      setRunStatus('ready');
    } catch (err: any) {
      setErrorMessage(err?.message ?? 'Failed to load scores');
      setRunStatus('error');
    }
  };

  const startRun = async () => {
    setErrorMessage(null);
    setRunStatus('running');
    setProgress({});
    setRows([]);
    setExpandedId(null);
    try {
      const { apiEvent } = await startBulkMatchScoring(dealParams);
      schedulePoll(apiEvent.id);
    } catch (err: any) {
      setErrorMessage(err?.message ?? 'Could not start the run');
      setRunStatus('error');
    }
  };

  const schedulePoll = (id: string) => {
    pollTimer.current = setTimeout(() => poll(id), POLL_INTERVAL_MS);
  };

  const poll = async (id: string) => {
    try {
      const status = await pollBulkMatchScoring(id);
      setProgress({ total: status.total, processed: status.processed });
      if (status.status === 'SUCCESS') {
        await loadPage(1, id);
      } else if (status.status === 'ERROR') {
        setErrorMessage(status.errorMessage ?? 'Run failed');
        setRunStatus('error');
      } else {
        schedulePoll(id);
      }
    } catch (err: any) {
      setErrorMessage(err?.message ?? 'Polling failed');
      setRunStatus('error');
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="buyer-profile-match__bulk">
      <p className="buyer-profile-match__blurb">
        Score this deal against every investor&rsquo;s buyer profile. Results
        are persisted &mdash; each run appends a fresh set of rows.
      </p>

      <div className="buyer-profile-match__bulk-actions">
        <Button
          primary
          loading={runStatus === 'running'}
          disabled={runStatus === 'running' || runStatus === 'loading'}
          onClick={startRun}
        >
          Run scoring for all investors
        </Button>
        {runStatus === 'running' && progress.total != null ? (
          <span className="buyer-profile-match__progress">
            {progress.processed ?? 0} / {progress.total} scored…
          </span>
        ) : null}
      </div>

      {errorMessage ? (
        <Message negative size="small">{errorMessage}</Message>
      ) : null}

      {rows.length === 0 && runStatus === 'ready' ? (
        <Message size="small">
          No saved scores yet for this deal. Click <em>Run scoring</em> above.
        </Message>
      ) : null}

      {rows.length > 0 ? (
        <>
          <Table compact selectable>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Investor</Table.HeaderCell>
                <Table.HeaderCell>Score</Table.HeaderCell>
                <Table.HeaderCell>Exclusion</Table.HeaderCell>
                <Table.HeaderCell>Scored at</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {rows.map((row) => (
                <Row
                  key={row.id}
                  row={row}
                  expanded={expandedId === row.id}
                  onToggle={() =>
                    setExpandedId((id) => (id === row.id ? null : row.id))
                  }
                  labelFor={labelFor}
                  categoryFor={categoryFor}
                />
              ))}
            </Table.Body>
          </Table>

          {pageCount > 1 ? (
            <div className="buyer-profile-match__pagination">
              <span className="buyer-profile-match__pagination-info">
                Page {page} of {pageCount} &middot; {total} investor
                {total === 1 ? '' : 's'}
              </span>
              <div className="buyer-profile-match__pagination-buttons">
                <Button
                  size="small"
                  disabled={page <= 1}
                  onClick={() => loadPage(page - 1, apiEventId ?? undefined)}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  disabled={page >= pageCount}
                  onClick={() => loadPage(page + 1, apiEventId ?? undefined)}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function Row({
  row,
  expanded,
  onToggle,
  labelFor,
  categoryFor,
}: {
  row: BulkMatchScoreRow;
  expanded: boolean;
  onToggle: () => void;
  labelFor: (questionKey: string) => string;
  categoryFor: (questionKey: string) => DealCategory | undefined;
}) {
  const scoredAt = useMemo(() => {
    try {
      return new Date(row.scoredAt).toLocaleString();
    } catch {
      return row.scoredAt;
    }
  }, [row.scoredAt]);

  return (
    <>
      <Table.Row onClick={onToggle} style={{ cursor: 'pointer' }}>
        <Table.Cell>
          <Icon name={expanded ? 'dropdown' : 'caret right'} />
          {row.investorCompany.name}
          {row.killed ? (
            <Label color="red" size="mini" style={{ marginLeft: 8 }}>
              Auto-killed
            </Label>
          ) : null}
          <Link
            to={`/company-profile/${row.investorCompany.id}?tab=buyer-profile`}
            target="_blank"
            rel="noopener noreferrer"
            className="buyer-profile-match__profile-link"
            title="View buyer profile"
            onClick={(e) => e.stopPropagation()}
          >
            <Icon name="external" />
          </Link>
        </Table.Cell>
        <Table.Cell>
          <Label
            color={scoreColor(row.score)}
            size="small"
            className="buyer-profile-match__score-badge"
          >
            {row.score}%
          </Label>
        </Table.Cell>
        <Table.Cell>
          <Label
            color={bandColor(row.exclusionBand)}
            size="small"
            className="buyer-profile-match__score-badge"
          >
            {row.exclusionTotal}
          </Label>
        </Table.Cell>
        <Table.Cell>{scoredAt}</Table.Cell>
      </Table.Row>
      {expanded ? (
        <Table.Row>
          <Table.Cell colSpan={4}>
            <div className="buyer-profile-match__result">
              <div className="buyer-profile-match__body">
                <div className="buyer-profile-match__overview">
                  <HeaderScore
                    score={row.result.score}
                    exclusionTotal={row.result.exclusionTotal}
                    exclusionBand={row.result.exclusionBand}
                    killed={row.result.killed}
                  />
                  <div className="buyer-profile-match__spider-card">
                    <SpiderChart categories={row.result.categories} />
                  </div>
                  <div className="buyer-profile-match__counts">
                    {row.result.contributions.length} field
                    {row.result.contributions.length === 1 ? '' : 's'} scored
                    {row.result.skipped.length
                      ? `, ${row.result.skipped.length} skipped`
                      : ''}
                  </div>
                </div>
                <div className="buyer-profile-match__detail">
                  <div className="buyer-profile-match__table-card">
                    <CategoryBreakdownTable
                      categories={row.result.categories}
                      contributions={row.result.contributions}
                      exclusions={row.result.exclusions}
                      skipped={row.result.skipped}
                      labelFor={labelFor}
                      categoryFor={categoryFor}
                    />
                  </div>
                  <NotesSection signals={row.result.signals} />
                </div>
              </div>
            </div>
          </Table.Cell>
        </Table.Row>
      ) : null}
    </>
  );
}
